import { useLingui } from '@lingui/react/macro';
import { Zap } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { Image } from '@/routes/-components/image';
import { EXPAND_STEP_ROOMY, Expand, ExpandGroup } from '../expand';
import { Karaoke } from '../karaoke';
import { DiamondGrid, Squiggle, diamondGlows } from '../primitives';

function RuleDot(props: { className?: string }) {
	return (
		<span aria-hidden className={cn('bg-olive relative size-2 shrink-0 rounded-full', props.className)}>
			<span className="bg-ink absolute top-1/2 left-1/2 size-1 -translate-x-1/2 -translate-y-1/2 rounded-full" />
		</span>
	);
}

/** The photos bleed off both edges at `xl`, hence positioned; on a phone a sliver would read as a mistake. */
export function Story() {
	const { t } = useLingui();
	// Not `overflow-hidden`: a scroll container would freeze every view timeline inside on its first frame.
	return (
		<section className="bg-ink relative overflow-clip text-white">
			{/* Held against the viewport: across two pinned scenes a section-wide texture slid behind frozen content. */}
			<div aria-hidden className="pointer-events-none absolute inset-0">
				<div className="sticky top-0 h-screen">
					<DiamondGrid
						color="rgba(255,255,255,0.35)"
						opacity={1}
						masks={[diamondGlows({ x: '50%', y: '22%' }, { x: '50%', y: '72%' })]}
					/>
				</div>
			</div>

			<div className="new-story-band relative mx-auto max-w-[1440px] px-6 py-20 md:py-28 xl:px-10">
				{/* The scroll the pin spends: `new-story-stage` in `-styles/theme.css`. */}
				<div className="new-story-stage">
					<div className="new-story-pin">
						<Karaoke
							className="mx-auto max-w-[1022px] text-center text-2xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]"
							text={t`Worldcoo was born in Barcelona in 2012 with a very clear purpose: to help NGOs find the funds they need to drive their projects forward.`}
						/>

						{/* Inside the held scene, or the pin separates it from the sentence; `theme.css` takes back its `pt-16`. */}
						<div aria-hidden className="new-story-rule hidden flex-col items-center pt-16 md:flex">
							<span className="new-story-rule-line bg-white/25" />
							<RuleDot className="new-story-rule-dot" />
						</div>
					</div>
				</div>

				{/* The stage carries the timeline: a pinned collage does not move, so its own would freeze (`new-story`). */}
				<div className="new-story-collage-stage">
					<div className="new-story-collage-pin">
						{/* Paces the below-`xl` arrival like the scroll-driven one. */}
						<ExpandGroup step={EXPAND_STEP_ROOMY} asChild>
							<div className="new-story relative mt-12 flex flex-col items-center gap-8 md:mt-20 xl:mt-32 xl:block xl:h-[489px]">
								{/* Placed from the card's left edge (`50% - 295px`), not the band's: lands on the export's -185 at 1440. */}
								<Expand index={0} asChild>
									<div className="new-story-piece new-story-photo new-story-photo-l w-full max-w-[578px] overflow-hidden rounded-3xl xl:absolute xl:top-[153px] xl:left-[calc(50%_-_905px)] xl:w-[578px]">
										<Image
											src="/redesign/about/boy-playground.jpg"
											alt={t`A boy playing in a playground`}
											height={786}
											aspectRatio={578 / 393}
											objectFit="cover"
											className="h-full w-full object-cover!"
										/>
									</div>
								</Expand>

								{/* `z-10` is load-bearing: the sequence's first half hides the photos behind it. */}
								<Expand index={1} asChild>
									<div className="new-story-piece new-story-card bg-apricot text-ink relative z-10 flex w-full max-w-[558px] flex-col gap-6 rounded-3xl p-8 xl:absolute xl:top-0 xl:left-1/2 xl:-ml-4 xl:min-h-[364px] xl:w-[558px] xl:-translate-x-1/2">
										<Zap className="text-brand size-8 shrink-0 fill-current" strokeWidth={0} />
										<Karaoke
											className="max-w-[494px] text-xl leading-[1.3] tracking-[-0.02em] md:text-2xl xl:text-[2rem]"
											text={t`Since then, we have been building innovative fundraising tools so that companies, their employees and their customers can add a small microdonation to their everyday transactions.`}
										/>
									</div>
								</Expand>

								<Expand index={2} asChild>
									<div className="new-story-piece new-story-photo new-story-photo-r w-full max-w-[385px] overflow-hidden rounded-3xl xl:absolute xl:top-[64px] xl:left-[calc(50%_+_295px)] xl:w-[385px]">
										<Image
											src="/redesign/about/girl-laughing.jpg"
											alt={t`A girl laughing outdoors`}
											height={786}
											aspectRatio={385 / 393}
											objectFit="cover"
											className="h-full w-full object-cover!"
										/>
									</div>
								</Expand>
							</div>
						</ExpandGroup>
					</div>
				</div>

				<p className="new-story-closing mx-auto mt-16 max-w-[943px] text-center text-xl leading-[1.3] font-medium md:mt-24 md:text-[2rem]">
					{t`Our premise is clear: to connect companies with `}
					<Squiggle lineClassName="text-olive">{t`social causes`}</Squiggle>
					{t` efficiently, securely and transparently.`}
				</p>
			</div>
		</section>
	);
}
